import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useOktaAuth } from '@okta/okta-react';
import {
  DebtorAccount,
  DebtorAccountContextType,
  DebtorCredentialResponse,
  DebtorPermission,
  DebtorSetting,
} from './DebtorAccountContext.types';
import { getDebtorCredentialAsync } from '@/app/shared/api/endpoints/debtorCredentials';
import { getDebtorSettingsAsync } from '@/app/shared/api/endpoints/debtorSettings';
import { configureOktaInstanceAsync } from '@/app/shared/api/endpoints/auth';
import { configureAuthOnlyInstance } from '@/app/shared/api/apiInstance';
import { queryClient } from '@/app/shared/api/queryClient';
import {
  readIsInternal,
  readSavedDebtorId,
  readSavedDebtorName,
  saveDebtorId,
  saveDebtorName,
  saveIsInternal,
} from '@/app/shared/utils/sessionStorage';

export const DebtorAccountContext = createContext<DebtorAccountContextType>({
  isInternal: false,
  selectedDebtor: null,
  selectedDebtorName: null,
  debtors: [],
  permissions: [],
  isLoading: false,
  isDebtorSwitching: false,
  error: null,
  selectDebtor: async () => {},
  initialized: false,
  debtorSettings: null,
});

const getAllPermissions = (): DebtorPermission[] =>
  Object.values(DebtorPermission).filter(
    (v) => typeof v === 'number',
  ) as DebtorPermission[];

export const DebtorAccountProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { authState } = useOktaAuth();
  const isAuthenticated = authState?.isAuthenticated ?? false;
  const accessToken = authState?.accessToken?.accessToken ?? '';

  const [isInternal, setIsInternal] = useState<boolean>(
    () => readIsInternal() ?? false,
  );
  const [selectedDebtor, setSelectedDebtor] = useState<DebtorAccount | null>(
    null,
  );
  const [selectedDebtorName, setSelectedDebtorName] = useState<string | null>(
    () => readSavedDebtorName(),
  );
  const [debtors, setDebtors] = useState<DebtorAccount[]>([]);
  const [permissions, setPermissions] = useState<DebtorPermission[]>(() =>
    readIsInternal() ? getAllPermissions() : [],
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDebtorSwitching, setIsDebtorSwitching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [debtorSettings, setDebtorSettings] = useState<DebtorSetting | null>(
    null,
  );

  const accessTokenRef = useRef(accessToken);
  const isInternalRef = useRef(isInternal);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    isInternalRef.current = isInternal;
  }, [isInternal]);

  const configureForDebtor = useCallback(async (debtorId: number) => {
    saveDebtorId(debtorId);
    await configureOktaInstanceAsync(accessTokenRef.current, String(debtorId));
  }, []);

  const selectDebtor = useCallback(
    async (debtor: DebtorAccount, debtorName?: string) => {
      if (debtor.debtorId === selectedDebtor?.debtorId) {
        return;
      }

      setIsDebtorSwitching(true);
      setError(null);

      try {
        await configureForDebtor(debtor.debtorId);
        setSelectedDebtor(debtor);

        if (debtorName) {
          setSelectedDebtorName(debtorName);
          saveDebtorName(debtorName);
        }

        setPermissions(
          isInternalRef.current ? getAllPermissions() : debtor.permissions,
        );

        const settings = await getDebtorSettingsAsync(debtor.debtorId);
        setDebtorSettings(settings);

        queryClient.invalidateQueries();
      } catch {
        setError('Failed to select debtor account.');
      } finally {
        setIsDebtorSwitching(false);
      }
    },
    [selectedDebtor?.debtorId, configureForDebtor],
  );

  useEffect(() => {
    if (!isAuthenticated || initialized || !accessToken) return;

    const initialize = async () => {
      setIsLoading(true);
      setError(null);

      try {
        configureAuthOnlyInstance({ token: accessToken });

        let credential: DebtorCredentialResponse;
        try {
          credential = await getDebtorCredentialAsync();
        } catch {
          setError('Unable to load account credentials.');
          return;
        }

        const internal = credential.isInternal;
        setIsInternal(internal);
        isInternalRef.current = internal;
        saveIsInternal(internal);
        setDebtors(credential.debtors);

        if (internal) {
          const savedDebtorId = readSavedDebtorId();
          const savedDebtorName = readSavedDebtorName();

          if (savedDebtorId !== null) {
            // Restore previously selected debtor from session
            const savedDebtor: DebtorAccount = {
              debtorId: savedDebtorId,
              permissions: [DebtorPermission.DebtorPortalAccess],
            };
            await configureForDebtor(savedDebtorId);
            setSelectedDebtor(savedDebtor);
            setSelectedDebtorName(savedDebtorName);
            const settings = await getDebtorSettingsAsync(savedDebtorId);
            setDebtorSettings(settings);
          } else if (credential.debtors.length > 0) {
            // Default to the first debtor returned from credentials
            const defaultDebtor = credential.debtors[0];
            await configureForDebtor(defaultDebtor.debtorId);
            setSelectedDebtor(defaultDebtor);
            saveDebtorId(defaultDebtor.debtorId);
            const settings = await getDebtorSettingsAsync(
              defaultDebtor.debtorId,
            );
            setDebtorSettings(settings);
          }

          setPermissions(getAllPermissions());
        } else if (credential.debtors.length > 0) {
          const debtor = credential.debtors[0];

          await configureForDebtor(debtor.debtorId);
          setSelectedDebtor(debtor);
          setPermissions(debtor.permissions);
          const settings = await getDebtorSettingsAsync(debtor.debtorId);
          setDebtorSettings(settings);
        } else {
          setError('No debtor accounts found for your user.');
        }

        setInitialized(true);
      } catch {
        setError('An unexpected error occurred during initialization.');
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [isAuthenticated, initialized, accessToken, configureForDebtor]);

  const value = useMemo(
    () => ({
      isInternal,
      selectedDebtor,
      selectedDebtorName,
      debtors,
      permissions,
      isLoading,
      isDebtorSwitching,
      error,
      selectDebtor,
      initialized,
      debtorSettings,
    }),
    [
      isInternal,
      selectedDebtor,
      selectedDebtorName,
      debtors,
      permissions,
      isLoading,
      isDebtorSwitching,
      error,
      selectDebtor,
      initialized,
      debtorSettings,
    ],
  );

  return (
    <DebtorAccountContext.Provider value={value}>
      {children}
    </DebtorAccountContext.Provider>
  );
};